/* import __COLOCATED_TEMPLATE__ from './case-record-type.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { get } from 'embercom/lib/ajax';
import { use } from 'ember-resources/util/function-resource';
import { AsyncData } from 'embercom/resources/utils/async-data';
import type IntlService from 'ember-intl/services/intl';
import type HandoffToSalesforceCaseType from 'embercom/models/operator/visual-builder/step/handoff-to-salesforce-case';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { IntegrationType } from 'embercom/objects/standalone/constants';

interface Args {
  step: HandoffToSalesforceCaseType;
}

type RecordType = {
  id: string;
  name: string;
};

type AvailableTypesResponse = {
  conversation: RecordType[];
};

export default class CaseRecordType extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;

  @use dataLoader = AsyncData<RecordType[]>(async () => {
    if (!this.integrationId) {
      return [];
    }

    let url = `/ember/standalone/integrations/${this.integrationType}/${this.integrationId}/available_types`;

    try {
      let response: AvailableTypesResponse = await get(url, { app_id: this.appId });
      // If we have a selected value, but it's not in the list, clear it.
      if (
        this.args.step.caseRecordTypeId &&
        !response.conversation.find(
          (caseRecordType: RecordType) => caseRecordType.id === this.args.step.caseRecordTypeId,
        )
      ) {
        this.args.step.caseRecordTypeId = null;
      }

      return response.conversation || [];
    } catch (error) {
      console.error('Failed to load Salesforce case record types:', error);
    }

    return [];
  });

  get appId() {
    return this.finStandaloneService.app.id;
  }

  get integrationId() {
    let integration = this.finStandaloneService.standaloneIntegrations.find(
      (int) => int.integrationType === this.integrationType,
    );
    return integration?.integrationId;
  }

  get integrationType() {
    return IntegrationType.SalesforceCases;
  }

  get items(): { text: string; value: string }[] {
    if (!this.dataLoader.value) {
      return [];
    }

    return this.dataLoader.value.map((caseRecordType: RecordType) => ({
      text: caseRecordType.name,
      value: caseRecordType.id,
    }));
  }

  get selectedValue() {
    if (!this.args.step.caseRecordTypeId) {
      return undefined;
    }

    let selectedItem = this.items.find((item) => item.value === this.args.step.caseRecordTypeId);
    return selectedItem ? selectedItem.value : undefined;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::HandoffToSalesforceCase::CaseRecordType': typeof CaseRecordType;
    'workflows/graph-editor/node-items/steps/handoff-to-salesforce-case/case-record-type': typeof CaseRecordType;
  }
}
